import type LogRocketSdk from 'logrocket'
import { APP_VERSION } from './appVersion'
import { envConfig } from './env'
import { sanitizeHeaders, sanitizeUrl } from './personalIdentifierInfo/pIISanitizeUtils'

/**
 * LogRocket (https://logrocket.com) is a session-replay + frontend debugging
 * platform: replays are tied to console logs, network requests and stack
 * traces, which makes it best-in-class for reproducing engineering bugs.
 *
 * SYN-7849 (POC: LogRocket session replay & debugging evaluation) — a hands-on
 * follow-up to the SYN-7807 analytics-tooling spike, isolating LogRocket in a
 * dedicated debugging role.
 *
 * The integration is enabled when `envConfig.LOGROCKET_APP_ID` is set (managed
 * by SRE per environment; see `docs/ENVIRONMENT_VARIABLES.md`). Empty disables
 * it everywhere.
 *
 * The SDK is loaded from LogRocket's official CDN by injecting a `<script>` tag
 * on demand (mirroring the Gainsight PX integration), rather than bundling the
 * npm package. LogRocket ships only a webpack-UMD build that Vite/Rollup cannot
 * cleanly code-split behind a dynamic `import()` (the async chunk comes out
 * empty), and the CDN loader is the vendor's recommended alternative. This
 * keeps the SDK entirely out of our bundle: when the POC is disabled nothing is
 * injected, fetched, or initialised — zero bundle cost and no network calls.
 *
 * Privacy-first defaults for our HR / recognition PII context: all form input
 * values, on-page text, and images are masked before anything leaves the browser
 * (`dom.inputSanitizer`, `dom.textSanitizer`, `dom.imageSanitizer`), the
 * `Authorization` session token is stripped from captured requests, and
 * request/response bodies are never recorded (they can carry names, emails and
 * recognition messages). See the write-up on SYN-7849.
 */

const LOGROCKET_SDK_URL = 'https://cdn.logr-in.com/LogRocket.min.js'

declare global {
  interface Window {
    LogRocket?: typeof LogRocketSdk
  }
}

export type LogRocketUser = {
  id: string
  role?: string | null
  createdAt?: string | null
}

export type LogRocketTenant = {
  id: string | number
  name?: string | null
  licenseType?: string | null
}

/** `[eventName, eventProperties?]`, exactly as passed to `LogRocket.track`. */
type TrackEventArgs = Parameters<typeof LogRocketSdk.track>

export const isLogRocketEnabled = (): boolean => envConfig.LOGROCKET_APP_ID !== ''

let isScriptInjected = false
let isInitialized = false
/** An identify call made before the CDN script finished loading, replayed on init. */
let pendingIdentify: (() => void) | null = null
/** Custom events tracked before the CDN script finished loading, replayed on init. */
let pendingTrackEvents: TrackEventArgs[] = []
/** Called after {@link resetLogRocketSession}, so per-session context can be re-sent. */
const sessionResetListeners = new Set<() => void>()

/** Configures LogRocket once the CDN script has loaded, then flushes any queued identify. */
const initLogRocket = (): void => {
  if (isInitialized || !window.LogRocket) return

  window.LogRocket.init(envConfig.LOGROCKET_APP_ID, {
    // Tie replays to the deployed app version, mirroring Rollbar / PostHog.
    release: APP_VERSION,
    // Session replay with PII protection: never record raw input values,
    // on-page text, or images (covers EmailChip, Froala, avatars, etc.).
    dom: {
      inputSanitizer: true,
      textSanitizer: true,
      imageSanitizer: true,
    },
    // Redact PII query params (e.g. loginHint, userEmail) from the page URLs
    // recorded in the replay timeline.
    browser: {
      urlSanitizer: sanitizeUrl,
    },
    // Capture network metadata for debugging context, but keep PII out of
    // LogRocket: redact PII query params from request URLs, strip the bearer
    // token from request headers, and drop request AND response bodies
    // entirely. Bodies can carry HR / recognition PII (names, emails,
    // recognition messages), and scrubbing them field-by-field is error-prone,
    // so for this POC we never record them at all.
    network: {
      requestSanitizer: request => {
        request.url = sanitizeUrl(request.url)
        sanitizeHeaders(request.headers)
        request.body = undefined
        return request
      },
      responseSanitizer: response => {
        // LogRocket may also record the URL on the response half of the same
        // capture (often identical to request.url). Sanitize it too so a second
        // copy of nested PII query params cannot leak.
        if (response.url !== undefined) response.url = sanitizeUrl(response.url)
        response.body = undefined
        return response
      },
    },
  })

  isInitialized = true

  if (pendingIdentify) {
    if (isLogRocketEnabled()) pendingIdentify()
    pendingIdentify = null
  }

  pendingTrackEvents.forEach(args => window.LogRocket?.track(...args))
  pendingTrackEvents = []
}

/**
 * Injects the LogRocket CDN loader. Safe to call multiple times — the script is
 * only injected once. No-op (nothing fetched) when the integration is disabled
 * (see {@link isLogRocketEnabled}) or when running outside a browser.
 */
export const loadLogRocket = (): void => {
  if (!isLogRocketEnabled()) return
  if (isScriptInjected) return
  if (typeof window === 'undefined' || typeof document === 'undefined') return

  const script = document.createElement('script')
  script.async = true
  script.crossOrigin = 'anonymous'
  script.src = LOGROCKET_SDK_URL
  script.onload = initLogRocket
  // The CDN fetch can fail (CSP block, network error, offline) and never fire
  // `onload`. Roll back so a later call can retry a fresh injection instead of
  // being permanently short-circuited by `isScriptInjected`. Any queued
  // identify is left in place so it replays if a subsequent attempt succeeds.
  script.onerror = () => {
    isScriptInjected = false
    script.remove()
  }

  const firstScript = document.getElementsByTagName('script')[0]
  if (firstScript && firstScript.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript)
  } else {
    document.head.appendChild(script)
  }

  isScriptInjected = true
}

/** Builds a LogRocket traits object, dropping null/undefined (unsupported) values. */
const toTraits = (entries: Record<string, string | number | boolean | null | undefined>) =>
  Object.fromEntries(
    Object.entries(entries).filter((entry): entry is [string, string | number | boolean] => {
      const value = entry[1]
      return value !== null && value !== undefined
    }),
  )

/**
 * Ends the current replay and starts a fresh LogRocket session, detaching the
 * recording from the previously identified user. Call this on logout: the SPA
 * does not reload between logout and a subsequent login on the same page load
 * (`clearAuth` only resets in-memory auth state), so without this the SDK would
 * keep recording into one session and a later `identify` would simply re-tag
 * that same session — blending multiple users (and the prior user's DOM /
 * network) into a single replay.
 *
 * Also drops any queued identify or custom events so a call made just before
 * logout cannot fire against the new session, then notifies
 * {@link onLogRocketSessionReset} listeners. Does not start a new session when
 * the SDK has not initialised yet (nothing has been recorded).
 */
export const resetLogRocketSession = (): void => {
  pendingIdentify = null
  pendingTrackEvents = []
  if (isInitialized && window.LogRocket) window.LogRocket.startNewSession()
  sessionResetListeners.forEach(listener => listener())
}

/**
 * Registers a listener run after every {@link resetLogRocketSession}, e.g. to
 * re-track the current page so the new session does not start without it.
 * Returns the unsubscribe function.
 */
export const onLogRocketSessionReset = (listener: () => void): (() => void) => {
  sessionResetListeners.add(listener)
  return () => {
    sessionResetListeners.delete(listener)
  }
}

/**
 * Reports a handled error to LogRocket so it is grouped in Issues with a stack
 * trace (resolved against the uploaded source maps).
 *
 * Needed because LogRocket only auto-captures errors that reach the window —
 * uncaught exceptions and unhandled promise rejections. A React error boundary
 * catches render-phase crashes before they get there, so without this call they
 * never reach Issues.
 *
 * No-op when the SDK has not initialised (nothing is being recorded).
 */
export const captureLogRocketException = (error: Error, componentStack?: string): void => {
  if (!isInitialized || !window.LogRocket) return
  window.LogRocket.captureException(
    error,
    componentStack ? { extra: { componentStack } } : undefined,
  )
}

/**
 * Records a custom event (https://docs.logrocket.com/reference/track), with
 * optional properties, so sessions can be filtered by it in the LogRocket
 * dashboard. Queued until the SDK has initialised, so events fired during page
 * load are not lost. No-op when the integration is disabled.
 */
export const trackLogRocketEvent = (...args: TrackEventArgs): void => {
  if (!isLogRocketEnabled()) return

  loadLogRocket()

  if (isInitialized && window.LogRocket) {
    window.LogRocket.track(...args)
  } else {
    pendingTrackEvents.push(args)
  }
}

/**
 * Associates the current replay session with a known user + tenant so replays
 * and errors are attributed to the right person and account. Ensures the SDK is
 * loading and queues the call if it has not finished initialising yet, so it
 * works even when called before the CDN script has loaded. No-op when the
 * integration is disabled.
 */
export const identifyLogRocket = (user: LogRocketUser, tenant: LogRocketTenant): void => {
  if (!isLogRocketEnabled()) return

  const traits = toTraits({
    role: user.role,
    created_at: user.createdAt,
    tenant_id: String(tenant.id),
    tenant_name: tenant.name,
    tenant_license_type: tenant.licenseType,
  })
  const runIdentify = () => window.LogRocket?.identify(user.id, traits)

  loadLogRocket()

  if (isInitialized && window.LogRocket) {
    runIdentify()
  } else {
    pendingIdentify = runIdentify
  }
}
